import { useId, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import type { RealEstateListing, RealEstateListingsResponse, WalletsResponse } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { useRealEstateListingsQuery, useWalletsQuery } from "../../api/queries";
import { useTownSession } from "../../app/TownSessionContext";
import { DIRECTORY_DISTRICTS, countStreets, districtName } from "../../lib/directoryFilters";
import { formatCents } from "../../lib/format";
import { formatCoins } from "../../lib/wallet";
import { plotCheckout } from "../../commerce/checkoutTargets";
import { cardPhrase, plotDollarAvailability, type DollarGate } from "../../commerce/gates";
import { buyAvailability } from "../../realEstate/purchase";
import { selectIsGuest, useTownStore } from "../../state/store";
import { useDollarGate } from "../checkout/useDollarGate";
import { PurchaseDialog } from "./PurchaseDialog";
import { useLivePayments, useTestPrices } from "../testPrices";

/**
 * Townsy Real Estate (civic office v-01): opens automatically while the visitor is inside the
 * office, can be dismissed and reopened from the HUD there. Lists every available plot from
 * `GET /real-estate/listings`; buying uses coins, or dollars (Townsy Credit first, then a card in
 * Stripe test mode) through the checkout. Purchasing always goes through an explicit review dialog.
 */
export function RealEstatePanel() {
  const session = useTownSession();
  const navigate = useNavigate();
  const { open, inOffice, selected, chatOpen, myHomeOpen } = useTownStore(
    useShallow((s) => ({
      chatOpen: s.ui.chatOpen,
      myHomeOpen: s.ui.myHomeOpen,
      open: s.ui.realEstateOpen,
      inOffice: Boolean(s.room.kind === "interior" && s.room.propertyId && session?.isRealEstateOffice(s.room.propertyId)),
      selected: s.ui.selectedPropertyId,
    })),
  );
  const isGuest = useTownStore(selectIsGuest);
  const setUi = useTownStore((s) => s.setUi);
  const visible = open && inOffice;
  const listingsQ = useRealEstateListingsQuery(inOffice, visible);
  const walletsQ = useWalletsQuery(inOffice && !isGuest);
  const dollar = useDollarGate();
  const openCheckout = useTownStore((s) => s.openCheckout);
  const [buying, setBuying] = useState<RealEstateListing | null>(null);
  // Mobile: one primary bottom sheet at a time (docs/01 section 6) — the property sheet wins.
  const small = typeof window !== "undefined" && window.innerWidth <= 900;
  // The My home manager uses the same side of the screen while it is open.
  if (!visible || myHomeOpen || (small && selected)) return null;

  const walletState: WalletState = isGuest ? "none" : walletsQ.data ? "ready" : walletsQ.error ? "error" : "loading";
  const owned = listingsQ.data?.ownedPropertyIds ?? [];
  const store = useTownStore.getState();
  // World v5: the owned plot names its district. ADR-0042: it is on the one town street, so "Go there" (leave the
  // office, frame the plot in Overview) is the same wherever it is.
  const properties = store.properties;

  return (
    <section
      className={`panel panel-left real-estate-panel${chatOpen ? " compact" : ""}`}
      role="region"
      aria-labelledby="real-estate-title"
      data-testid="real-estate-panel"
    >
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="real-estate-title">Townsy Real Estate</h2>
          <div className="small muted">1 Plaza Way · Residential plots for sale</div>
        </div>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close Real Estate listings" onClick={() => setUi({ realEstateOpen: false })}>
          ×
        </button>
      </div>
      <div className="panel-body">
        <RealEstateListingsView
          state={listingsQ.isPending ? "loading" : listingsQ.error ? "error" : "ready"}
          error={listingsQ.error ? errorMessage(listingsQ.error) : null}
          onRetry={() => void listingsQ.refetch()}
          data={listingsQ.data ?? null}
          isGuest={isGuest}
          wallets={walletsQ.data ?? null}
          walletState={walletState}
          ownedAddresses={owned.map((id) => {
            const p = properties[id];
            return p ? `${p.address}, ${districtName(p.districtId)}` : id;
          })}
          onGoToOwned={owned[0] ? () => session?.goToProperty(owned[0]!) : null}
          onDetails={(id) => navigate(`/place/${id}`)}
          onBuy={setBuying}
          dollar={dollar}
          onBuyUsd={(listing) => openCheckout(plotCheckout(listing))}
        />
      </div>
      {buying && walletsQ.data ? (
        <PurchaseDialog
          listing={buying}
          coinsAvailable={walletsQ.data.coins.available}
          onClose={() => setBuying(null)}
          onStale={() => session?.refreshAfterPurchaseChange()}
          onPurchased={(res) => {
            setBuying(null);
            session?.afterPlotPurchase(res, buying.address);
          }}
        />
      ) : null}
    </section>
  );
}

export type WalletState = "loading" | "ready" | "error" | "none";

/** Presentational listings (pure props; unit-tested with server rendering). */
export function RealEstateListingsView({
  state,
  error,
  onRetry,
  data,
  isGuest,
  wallets,
  walletState,
  ownedAddresses,
  onGoToOwned,
  onDetails,
  onBuy,
  dollar,
  onBuyUsd,
}: {
  state: "loading" | "error" | "ready";
  error: string | null;
  onRetry(): void;
  data: RealEstateListingsResponse | null;
  isGuest: boolean;
  wallets: WalletsResponse | null;
  walletState: WalletState;
  ownedAddresses: string[];
  /** "Go there" (in the town): leave the office and frame the owned plot from above, whichever district it is in. */
  onGoToOwned: (() => void) | null;
  onDetails(propertyId: string): void;
  onBuy(listing: RealEstateListing): void;
  /** Card + Townsy Credit availability (payments config + viewer); the listing's own block is added here. */
  dollar: DollarGate;
  onBuyUsd(listing: RealEstateListing): void;
}) {
  const testPrices = useTestPrices();
  const card = cardPhrase(useLivePayments() ? "live" : "test");
  const usd = data ? plotDollarAvailability(data, dollar) : dollar;
  // World v3 has 40 plots on four lanes: list them by lane, with a lane filter. World v5 (ADR-0041): grouped by
  // district (Central, Riverside, Hillcrest), with a district filter before the lane filter.
  const [district, setDistrict] = useState<string | null>(null);
  const [street, setStreet] = useState<string | null>(null);
  const streetId = useId();
  const districtId = useId();
  const districts = useMemo(() => (data ? listingDistricts(data.items) : []), [data]);
  const inDistrict = useMemo(() => (data ? data.items.filter((l) => !district || l.districtId === district) : []), [data, district]);
  const streets = useMemo(() => countStreets(inDistrict.map((l) => l.street)), [inDistrict]);
  const groups = useMemo(() => {
    const rank = new Map(streets.map((s, i) => [s.name, i]));
    const shownItems = inDistrict
      .filter((l) => !street || l.street === street)
      .map((l, i) => ({ l, i }))
      .sort((a, b) => (rank.get(a.l.street) ?? 99) - (rank.get(b.l.street) ?? 99) || a.i - b.i)
      .map((x) => x.l);
    return districts.map((d) => ({ ...d, items: shownItems.filter((l) => l.districtId === d.id) })).filter((g) => g.items.length > 0);
  }, [inDistrict, street, streets, districts]);
  const shownCount = groups.reduce((n, g) => n + g.items.length, 0);
  const plotsRule = data
    ? ` Each account can own ${data.plotsPerAccount === 1 ? "one plot" : `${data.plotsPerAccount} plots`}${districts.length > 1 ? " in the whole town, whichever district it is in" : ""}.`
    : "";
  return (
    <div className="stack">
      <p className="small" style={{ margin: 0 }}>
        Buy an empty plot and your home starts as a tent. Homes can never be taken over by other players.
        {plotsRule}
      </p>
      {data?.blockedReason === "SIGN_IN_REQUIRED" ? (
        <div className="notice notice-info small">
          Guests can look around. <Link to="/account">Sign in or create an account</Link> to buy a plot.
        </div>
      ) : null}
      {data?.blockedReason === "EMAIL_NOT_VERIFIED" ? (
        <div className="notice notice-warning small">Verify your email address to buy a plot. Check your inbox for the verification link.</div>
      ) : null}
      {ownedAddresses.length > 0 ? (
        <div className="notice notice-info small" data-testid="real-estate-owned">
          You own {ownedAddresses.join("; ")}.{" "}
          {onGoToOwned ? (
            <button type="button" className="btn btn-sm" onClick={onGoToOwned} data-testid="real-estate-go-owned">
              Go there
            </button>
          ) : null}
        </div>
      ) : null}
      {!isGuest && wallets ? (
        <p className="small muted" style={{ margin: 0 }}>
          Your coins: <strong className="coin-text">{formatCoins(wallets.coins.available)}</strong>. {wallets.earning.message}
        </p>
      ) : null}

      {state === "loading" ? (
        <p className="muted">Loading listings…</p>
      ) : state === "error" ? (
        <div className="notice notice-danger" role="alert">
          Couldn't load the listings: {error}{" "}
          <button type="button" className="btn btn-sm" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : data && data.items.length === 0 ? (
        <p className="muted">Every plot is taken right now. Check back later.</p>
      ) : data ? (
        <>
        <div className="row listing-filters">
          {districts.length > 1 ? (
            <label className="directory-street" htmlFor={districtId}>
              <span className="small">District</span>
              <select
                id={districtId}
                value={district ?? ""}
                onChange={(e) => {
                  const next = e.target.value || null;
                  setDistrict(next);
                  // Lanes belong to one district: a lane of another district no longer applies.
                  if (next && street && !data.items.some((l) => l.districtId === next && l.street === street)) setStreet(null);
                }}
                data-testid="listing-district"
              >
                <option value="">All districts ({data.items.length})</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.count})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {streets.length > 1 ? (
            <label className="directory-street" htmlFor={streetId}>
              <span className="small">Street</span>
              <select id={streetId} value={street ?? ""} onChange={(e) => setStreet(e.target.value || null)} data-testid="listing-street">
                <option value="">All streets ({inDistrict.length})</option>
                {streets.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name} ({s.count})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <span className="small muted" role="status" data-testid="listing-count">
            {listingCountText(shownCount, street, district ? (districts.find((d) => d.id === district)?.name ?? district) : null)}
          </span>
        </div>
        <div className="stack listing-groups" data-testid="real-estate-listings">
          {groups.map((group) => (
            <section
              key={group.id}
              className="listing-group"
              aria-labelledby={districts.length > 1 ? `${districtId}-${group.id}` : undefined}
              data-testid={`listing-district-${group.id}`}
            >
              {districts.length > 1 ? (
                <h3 id={`${districtId}-${group.id}`} className="listing-group-title">
                  {group.name} <span className="small muted">· {group.items.length === 1 ? "1 plot" : `${group.items.length} plots`}</span>
                </h3>
              ) : null}
              <ul className="listing-list">
                {group.items.map((listing) => {
                  const avail = buyAvailability({ listings: data, listing, wallets, walletState });
                  const reasonId = `buy-reason-${listing.propertyId}`;
                  const showUsdReason = listing.offer.usdCents !== null && (usd.enabled || usd.reason !== avail.reason);
                  return (
                    <li key={listing.propertyId} className="listing-item" data-testid={`listing-${listing.propertyId}`}>
                      <div className="listing-head">
                        <div style={{ minWidth: 0 }}>
                          <strong>{listing.address}</strong>
                          <div className="small muted">{listing.street}</div>
                        </div>
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onDetails(listing.propertyId)} title="Show this plot's details">
                          Details
                        </button>
                      </div>
                      <div className="row" style={{ gap: 6 }}>
                        {listing.offer.coins !== null ? (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={!avail.enabled}
                            aria-describedby={avail.reason ? reasonId : undefined}
                            onClick={() => onBuy(listing)}
                            data-testid={`buy-coins-${listing.propertyId}`}
                          >
                            Buy for {formatCoins(listing.offer.coins)}
                          </button>
                        ) : null}
                        {listing.offer.usdCents !== null ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={!usd.enabled}
                            title={usd.reason ?? `Townsy Credit first, then ${card}`}
                            aria-describedby={showUsdReason ? `usd-${listing.propertyId}` : avail.reason ? reasonId : undefined}
                            onClick={() => onBuyUsd(listing)}
                            data-testid={`buy-usd-${listing.propertyId}`}
                          >
                            Buy for {formatCents(listing.offer.usdCents)}
                          </button>
                        ) : null}
                      </div>
                      {avail.reason ? (
                        <span id={reasonId} className="hint" data-testid={`buy-reason-${listing.propertyId}`}>
                          {avail.reason}
                        </span>
                      ) : null}
                      {showUsdReason ? (
                        <span id={`usd-${listing.propertyId}`} className="hint" data-testid={`buy-usd-reason-${listing.propertyId}`}>
                          {usd.enabled ? `Dollar price: Townsy Credit first, then ${card}.` : `Dollar price: ${usd.reason}`}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {groups.length === 0 ? <p className="muted small">No plots for sale match these filters.</p> : null}
        </div>
        </>
      ) : null}
      <p className="hint" style={{ margin: 0 }}>
        {testPrices ? "Development test prices. " : null}A plot's coin price and dollar price are two separate offers, not an exchange rate.
      </p>
    </div>
  );
}

/** The districts that have plots for sale, in manifest order (Central, Riverside, Hillcrest), with their counts. */
export function listingDistricts(items: readonly Pick<RealEstateListing, "districtId" | "districtName">[]): { id: string; name: string; count: number }[] {
  const counts = new Map<string, { name: string; count: number }>();
  for (const l of items) {
    const c = counts.get(l.districtId);
    if (c) c.count++;
    else counts.set(l.districtId, { name: l.districtName, count: 1 });
  }
  const rank = new Map(DIRECTORY_DISTRICTS.map((d, i) => [d.id, i]));
  return [...counts.entries()]
    .map(([id, c]) => ({ id, name: c.name, count: c.count }))
    .sort((a, b) => (rank.get(a.id) ?? rank.size) - (rank.get(b.id) ?? rank.size) || a.name.localeCompare(b.name));
}

/** "77 plots for sale", "20 plots for sale in Riverside", "1 plot for sale on Alder Lane in Riverside". */
export function listingCountText(count: number, street: string | null, district: string | null): string {
  return `${count === 1 ? "1 plot for sale" : `${count} plots for sale`}${street ? ` on ${street}` : ""}${district ? ` in ${district}` : ""}`;
}
